/**
 * CMS hooks for fetching Wagtail content.
 */

import { useEffect, useState, useMemo } from 'react';
import type { AxiosError } from 'axios';
import { apiConfig, axiosInstance } from '@/api/client';
import { CmsApi } from '@/api/generated';
import type { PageTree } from '@/api/generated';

// Shared CMS API instance (stateless, safe to share)
const cmsApi = new CmsApi(apiConfig, '', axiosInstance);

// Cache for page existence checks with in-flight deduplication
const pageExistsCache = new Map<string, boolean>();
const pageExistsInFlight = new Map<string, Promise<boolean>>();

async function checkPageExists(path: string): Promise<boolean> {
  // Return cached result
  const cached = pageExistsCache.get(path);
  if (cached !== undefined) return cached;

  // Return in-flight promise if already fetching
  const inFlight = pageExistsInFlight.get(path);
  if (inFlight) return inFlight;

  // Create new fetch promise
  const promise = (async () => {
    try {
      const response = path
        ? await cmsApi.cmsPageTreeByPath(path)
        : await cmsApi.cmsPageTreeRoot();
      const exists = response.data !== null;
      pageExistsCache.set(path, exists);
      return exists;
    } catch {
      pageExistsCache.set(path, false);
      return false;
    } finally {
      pageExistsInFlight.delete(path);
    }
  })();

  pageExistsInFlight.set(path, promise);
  return promise;
}

/**
 * Fetch any CMS page by its path.
 * - usePage() returns the home page
 * - usePage('blog') returns the blog index
 * - usePage('blog/my-post') returns a blog post
 */
export function usePage(path: string = '') {
  const [data, setData] = useState<PageTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const normalizedPath = useMemo(() => path.replace(/^\/+|\/+$/g, ''), [path]);

  useEffect(() => {
    let cancelled = false;

    async function fetchPage() {
      setLoading(true);
      setError(null);
      try {
        const response = normalizedPath
          ? await cmsApi.cmsPageTreeByPath(normalizedPath)
          : await cmsApi.cmsPageTreeRoot();
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (cancelled) return;
        const axiosErr = err as AxiosError;
        if (axiosErr.response?.status === 404) {
          setError('Page not found');
        } else {
          setError(axiosErr.message || 'Failed to load page');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPage();
    return () => {
      cancelled = true;
    };
  }, [normalizedPath]);

  return { data, loading, error };
}

/**
 * Check if a CMS page exists at a given path.
 * Results are cached and in-flight requests are deduplicated.
 */
export function usePageExists(path: string) {
  const normalizedPath = useMemo(() => path.replace(/^\/+|\/+$/g, ''), [path]);
  const cached = pageExistsCache.get(normalizedPath);

  const [exists, setExists] = useState<boolean | null>(cached ?? null);
  const [loading, setLoading] = useState(cached === undefined);

  useEffect(() => {
    // Skip if already have result
    if (cached !== undefined) return;

    let cancelled = false;
    checkPageExists(normalizedPath).then((result) => {
      if (!cancelled) {
        setExists(result);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [normalizedPath, cached]);

  return { exists, loading };
}
